import { RETRY_CONFIG } from '../constants/apiConfig';

/**
 * Utility function to retry API calls with exponential backoff
 * Performs up to maxRetries retry attempts after the initial request fails
 * @param {Function} fn - The function to retry (should return a Promise)
 * @param {number} maxRetries - Maximum number of retry attempts after initial failure (defaults to 3)
 * @param {number} baseDelay - Base delay in milliseconds (defaults to config)
 * @returns {Promise} - The result of the function call
 */
export const retryWithBackoff = async (fn, maxRetries = RETRY_CONFIG.maxRetries, baseDelay = RETRY_CONFIG.baseDelay) => {
    // If retry is disabled, just call the function once
    if (!RETRY_CONFIG.enabled) {
        return await fn();
    }
    
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            if (attempt > 0) {
                console.log(`🔄 RETRY ATTEMPT ${attempt} of ${maxRetries}`);
            }
            return await fn();
        } catch (error) {
            lastError = error;
            
            // Don't retry on abort errors (user canceled) or explicitly non-retryable errors
            if (error.name === 'AbortError' || error.isRetryable === false) {
                throw error;
            }
            
            // If this was the last attempt, throw the error (no logout)
            if (attempt === maxRetries) {
                console.error(`❌ API CALL FAILED after ${maxRetries} retries (${maxRetries + 1} total attempts):`, error.message);
                throw error;
            }
            
            // Calculate delay with exponential backoff
            const delay = baseDelay * Math.pow(2, attempt);
            console.warn(`⚠️ Attempt ${attempt + 1}/${maxRetries + 1} failed. Retrying in ${delay}ms...`, error.message);
            
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    }
    
    // This should never be reached, but just in case
    throw lastError;
};